import { t as getLogger } from "./logger-CpoV6RQj.mjs";
import { i as createSlowRequestWarning, n as WINDOW, r as createNetworkError, t as getErrorManager } from "./errorManager-CoSVVhi4.mjs";

//#region src/interceptors/shared/isAbsoluteUrl.ts
/**
* Checks if the provided URL is absolute using the URL constructor.
* If the URL is relative (or invalid), the constructor will throw.
*
* @param url - The URL to check.
* @returns True if the URL is absolute, false otherwise.
*/
function isAbsoluteUrl(url) {
	try {
		new URL(url);
		return true;
	} catch {
		return false;
	}
}

//#endregion
//#region src/interceptors/shared/url.ts
/**
* Parse and normalize a URL
*/
function parseUrl(url, currentOrigin) {
	let urlString;
	let urlObj = null;
	let isRelative = false;
	if (url instanceof URL) {
		urlString = url.toString();
		urlObj = url;
	} else {
		urlString = url;
		isRelative = !isAbsoluteUrl(urlString);
		if (!isRelative) try {
			urlObj = new URL(urlString);
		} catch {
			return null;
		}
		else if (currentOrigin) try {
			const normalizedPath = urlString.startsWith("/") ? urlString : `/${urlString}`;
			urlObj = new URL(normalizedPath, currentOrigin);
		} catch {
			return null;
		}
	}
	return {
		urlString,
		urlObj,
		isRelative,
		urlPath: urlObj?.pathname || "",
		urlHost: urlObj?.host || ""
	};
}
/**
* Check if URL matches any pattern
*/
function matchPatterns(patterns, urlInfo) {
	const { urlString, urlObj, urlPath, urlHost } = urlInfo;
	for (const pattern of patterns) {
		if (pattern instanceof RegExp) {
			if (pattern.test(urlString)) return true;
			continue;
		}
		const patternStr = pattern;
		if (!patternStr) continue;
		if (patternStr === urlString || urlObj && patternStr === urlObj.href) return true;
		if (patternStr.startsWith("/") && patternStr === urlPath) return true;
		if (urlObj && patternStr === urlHost) return true;
		if (patternStr.includes("*")) {
			if (patternStr.startsWith("*.") && urlHost.endsWith(patternStr.slice(2))) return true;
			if (patternStr.endsWith("*")) {
				const prefix = patternStr.slice(0, -1);
				if (patternStr.startsWith("/") && urlPath.startsWith(prefix) || urlString.startsWith(prefix)) return true;
			}
			continue;
		}
		if (!patternStr.startsWith("/") && !patternStr.includes("://")) {
			if (urlString.includes(patternStr)) return true;
		}
	}
	return false;
}

//#endregion
//#region src/interceptors/shared/isMonitoredService.ts
/**
* Checks if a URL matches any of the provided patterns with complete error isolation
*
* @param url - The URL to check (string or URL object)
* @param patterns - Optional patterns to match against
* @param options - Configuration options
* @returns True if the URL matches, false otherwise (never throws)
*/
function isMonitoredService(url, patterns, options = {}) {
	try {
		if (!url) return false;
		let urlString;
		try {
			urlString = typeof url === "string" ? url : url.toString();
		} catch {
			return false;
		}
		if (!urlString || urlString.trim().length === 0) return false;
		const safeOptions = validateOptions(options);
		let urlInfo;
		try {
			urlInfo = parseUrl(urlString, safeOptions.currentOrigin);
		} catch {
			return false;
		}
		if (!urlInfo) return false;
		if (patterns?.length) try {
			if (matchPatterns(patterns, urlInfo)) return true;
		} catch {}
		if (safeOptions.requireExplicitPatterns) return false;
		if (urlInfo.isRelative) return safeOptions.treatRelativeAsInternal;
		if (urlInfo.urlObj && safeOptions.includeCurrentOrigin && safeOptions.currentOrigin && urlInfo.urlObj.origin === safeOptions.currentOrigin) return true;
		return false;
	} catch {
		return false;
	}
}
/**
* Validate and sanitize options with safe defaults
*/
function validateOptions(options) {
	try {
		return {
			currentOrigin: getCurrentOrigin(options.currentOrigin),
			includeCurrentOrigin: typeof options.includeCurrentOrigin === "boolean" ? options.includeCurrentOrigin : true,
			treatRelativeAsInternal: typeof options.treatRelativeAsInternal === "boolean" ? options.treatRelativeAsInternal : true,
			requireExplicitPatterns: typeof options.requireExplicitPatterns === "boolean" ? options.requireExplicitPatterns : false
		};
	} catch {
		return {
			currentOrigin: "",
			includeCurrentOrigin: true,
			treatRelativeAsInternal: true,
			requireExplicitPatterns: false
		};
	}
}
/**
* Safely get current origin with fallbacks
*/
function getCurrentOrigin(providedOrigin) {
	if (providedOrigin && typeof providedOrigin === "string" && providedOrigin.trim().length > 0) return providedOrigin.trim();
	try {
		if (WINDOW.location?.origin) return WINDOW.location.origin;
	} catch {}
	return "";
}
/**
* Creates a pre-configured URL matcher with enterprise-level error handling
*/
function createMonitoredServiceChecker(defaultPatterns = [], defaultOptions = {}) {
	const compiledPatterns = compilePatterns(defaultPatterns);
	const safeDefaultOptions = validateOptions(defaultOptions);
	return (url, additionalPatterns = [], overrideOptions = {}) => {
		try {
			const compiledAdditionalPatterns = compilePatterns(additionalPatterns);
			return isMonitoredService(url, [...compiledPatterns, ...compiledAdditionalPatterns], {
				...safeDefaultOptions,
				...overrideOptions
			});
		} catch {
			return false;
		}
	};
}
/**
* Safely compile patterns with error handling
*/
function compilePatterns(patterns) {
	if (!Array.isArray(patterns)) return [];
	const compiled = [];
	for (const pattern of patterns) try {
		const compiledPattern = compilePattern(pattern);
		if (compiledPattern !== null) compiled.push(compiledPattern);
	} catch {
		continue;
	}
	return compiled;
}
/**
* Compile string patterns with wildcards to RegExp safely
*/
function compilePattern(pattern) {
	try {
		if (pattern instanceof RegExp) return pattern;
		if (typeof pattern !== "string" || pattern.length === 0) return null;
		if (!pattern.includes("*")) return pattern;
		const escaped = pattern.replace(/[.+?^${}()|[\]\\]/g, "\\$&").replace(/\*/g, ".*");
		return /* @__PURE__ */ new RegExp("^" + escaped + "$");
	} catch {
		return null;
	}
}

//#endregion
//#region src/interceptors/shared/requestMonitor.ts
/**
* Singleton service that centralizes request monitoring logic
* Enterprise-level implementation with complete error isolation
*/
var RequestMonitor = class RequestMonitor {
	static instance;
	config;
	isMonitoredFn;
	logger = getLogger();
	errorManager;
	isInitialized = false;
	constructor(config = {}) {
		this.initializeConfig(config);
		this.initializeUrlChecker();
		this.initializeErrorManager();
	}
	/**
	* Initialize configuration with safe defaults
	*/
	initializeConfig(config) {
		try {
			this.config = {
				timeoutMs: this.validateTimeout(config.timeoutMs),
				showSlowRequestAlerts: config.showSlowRequestAlerts ?? true,
				showErrorAlerts: config.showErrorAlerts ?? true,
				monitoredServicePatterns: this.validatePatterns(config.monitoredServicePatterns),
				monitoredServiceOptions: config.monitoredServiceOptions ?? {}
			};
		} catch (error) {
			this.logger.error("Failed to initialize RequestMonitor config:", error);
			this.config = {
				timeoutMs: 3e4,
				showSlowRequestAlerts: false,
				showErrorAlerts: false,
				monitoredServicePatterns: [],
				monitoredServiceOptions: {}
			};
		}
	}
	/**
	* Validate and sanitize timeout value
	*/
	validateTimeout(timeout) {
		if (typeof timeout === "number" && timeout > 0 && timeout <= 3e5) return Math.floor(timeout);
		return 3e4;
	}
	/**
	* Validate and sanitize patterns array
	*/
	validatePatterns(patterns) {
		if (!Array.isArray(patterns)) return [];
		return patterns.filter((pattern) => {
			try {
				return typeof pattern === "string" || pattern instanceof RegExp;
			} catch {
				this.logger.warn("Invalid pattern in monitoredServicePatterns:", pattern);
				return false;
			}
		});
	}
	/**
	* Initialize URL checker with error handling
	*/
	initializeUrlChecker() {
		try {
			this.isMonitoredFn = createMonitoredServiceChecker(this.config.monitoredServicePatterns, this.config.monitoredServiceOptions);
		} catch (error) {
			this.logger.error("Failed to initialize URL checker:", error);
			this.isMonitoredFn = () => false;
		}
	}
	/**
	* Initialize error manager with error handling
	*/
	initializeErrorManager() {
		try {
			this.errorManager = getErrorManager();
			this.isInitialized = true;
		} catch (error) {
			this.logger.error("Failed to initialize error manager:", error);
			this.isInitialized = false;
		}
	}
	/**
	* Get the singleton instance of RequestMonitor
	*/
	static getInstance(config) {
		if (!RequestMonitor.instance) try {
			RequestMonitor.instance = new RequestMonitor(config);
		} catch (error) {
			getLogger().error("Failed to create RequestMonitor instance:", error);
			RequestMonitor.instance = new RequestMonitor({
				showSlowRequestAlerts: false,
				showErrorAlerts: false
			});
		}
		else if (config) RequestMonitor.instance.updateConfig(config);
		return RequestMonitor.instance;
	}
	/**
	* Update the configuration safely
	*/
	updateConfig(config) {
		if (!config || typeof config !== "object") {
			this.logger.warn("Invalid config provided to updateConfig");
			return;
		}
		try {
			if ("timeoutMs" in config) this.config.timeoutMs = this.validateTimeout(config.timeoutMs);
			if (typeof config.showSlowRequestAlerts === "boolean") this.config.showSlowRequestAlerts = config.showSlowRequestAlerts;
			if (typeof config.showErrorAlerts === "boolean") this.config.showErrorAlerts = config.showErrorAlerts;
			if ("monitoredServicePatterns" in config || "monitoredServiceOptions" in config) {
				if ("monitoredServicePatterns" in config) this.config.monitoredServicePatterns = this.validatePatterns(config.monitoredServicePatterns);
				if ("monitoredServiceOptions" in config && config.monitoredServiceOptions) this.config.monitoredServiceOptions = { ...config.monitoredServiceOptions };
				this.initializeUrlChecker();
			}
		} catch (error) {
			this.logger.error("Failed to update RequestMonitor config:", error);
		}
	}
	/**
	* Check if a URL should be monitored - completely safe method
	*/
	isMonitored(url) {
		try {
			if (!url || typeof url !== "string" || url.trim().length === 0) return false;
			return this.isMonitoredFn(url.trim());
		} catch (error) {
			this.logger.warn("Error checking if URL is monitored:", error);
			return false;
		}
	}
	/**
	* Handle slow request warning with complete error isolation
	*/
	handleSlowRequest(url, source) {
		if (!this.validateInputs(url, source) || !this.isInitialized) return;
		try {
			if (!this.config.showSlowRequestAlerts || !this.config.showErrorAlerts) return;
			const shouldMonitor = this.isMonitored(url);
			queueMicrotask(() => {
				this.executeSlowRequestHandler(url, source, shouldMonitor);
			});
		} catch (error) {
			this.logger.warn("Error in handleSlowRequest:", error);
		}
	}
	/**
	* Execute slow request handler in isolated context
	*/
	executeSlowRequestHandler(url, source, shouldMonitor) {
		try {
			const timeoutError = createSlowRequestWarning(`${source} request is taking longer than ${this.config.timeoutMs}ms`, url, shouldMonitor, this.config.timeoutMs);
			if (this.errorManager && typeof this.errorManager.handleError === "function") this.errorManager.handleError(timeoutError, source);
		} catch (error) {
			this.logger.warn(`Error in slow request handler for ${source}:`, error);
		}
	}
	/**
	* Handle HTTP error with complete error isolation
	*/
	handleHttpError(status, statusText, url, source) {
		if (!this.validateInputs(url, source) || !this.isInitialized) return;
		try {
			if (!this.config.showErrorAlerts) return;
			if (!this.isValidStatusCode(status)) {
				this.logger.warn(`Invalid status code in handleHttpError: ${status} (type: ${typeof status})`);
				return;
			}
			if (status < 500 || status >= 600) return;
			const shouldMonitor = this.isMonitored(url);
			const safeStatusText = this.sanitizeStatusText(statusText);
			queueMicrotask(() => {
				this.executeHttpErrorHandler(status, safeStatusText, url, source, shouldMonitor);
			});
		} catch (error) {
			this.logger.warn("Error in handleHttpError:", error);
		}
	}
	/**
	* Execute HTTP error handler in isolated context
	*/
	executeHttpErrorHandler(status, statusText, url, source, shouldMonitor) {
		try {
			const error = createNetworkError(`${source} error: ${status} ${statusText}`, url, shouldMonitor);
			if (this.errorManager && typeof this.errorManager.handleError === "function") this.errorManager.handleError(error, source);
		} catch (error) {
			this.logger.warn(`Error in HTTP error handler for ${source}:`, error);
		}
	}
	/**
	* Handle network error with complete error isolation
	*/
	handleNetworkError(message, url, source, originalError) {
		if (!this.validateInputs(url, source) || !this.isInitialized) return;
		try {
			if (!this.config.showErrorAlerts) return;
			const safeMessage = this.sanitizeMessage(message);
			if (!safeMessage) {
				this.logger.warn("Invalid message in handleNetworkError");
				return;
			}
			const shouldMonitor = this.isMonitored(url);
			queueMicrotask(() => {
				this.executeNetworkErrorHandler(safeMessage, url, source, shouldMonitor, originalError);
			});
		} catch (error) {
			this.logger.warn("Error in handleNetworkError:", error);
		}
	}
	/**
	* Execute network error handler in isolated context
	*/
	executeNetworkErrorHandler(message, url, source, shouldMonitor, originalError) {
		try {
			const error = createNetworkError(message, url, shouldMonitor, originalError);
			if (this.errorManager && typeof this.errorManager.handleError === "function") this.errorManager.handleError(error, source);
		} catch (error) {
			this.logger.warn(`Error in network error handler for ${source}:`, error);
		}
	}
	/**
	* Validate common inputs
	*/
	validateInputs(url, source) {
		if (!url || typeof url !== "string" || url.trim().length === 0) {
			this.logger.warn("Invalid URL in request monitor");
			return false;
		}
		if (!source || typeof source !== "string" || source.trim().length === 0) {
			this.logger.warn("Invalid source in request monitor");
			return false;
		}
		return true;
	}
	/**
	* Validate status code
	*/
	isValidStatusCode(status) {
		return typeof status === "number" && Number.isInteger(status) && status >= 100 && status < 600;
	}
	/**
	* Sanitize status text
	*/
	sanitizeStatusText(statusText) {
		if (typeof statusText === "string" && statusText.trim().length > 0) return statusText.trim().substring(0, 200);
		return "Unknown error";
	}
	/**
	* Sanitize message
	*/
	sanitizeMessage(message) {
		if (typeof message === "string" && message.trim().length > 0) return message.trim().substring(0, 500);
		return "";
	}
	/**
	* Get the current timeout configuration
	*/
	getTimeoutMs() {
		try {
			return this.config.timeoutMs;
		} catch (error) {
			this.logger.warn("Error getting timeout:", error);
			return 3e4;
		}
	}
};
/**
* Get the shared request monitor instance
*/
function getRequestMonitor(config) {
	try {
		return RequestMonitor.getInstance(config);
	} catch (error) {
		getLogger().error("Failed to get RequestMonitor instance:", error);
		return RequestMonitor.getInstance({
			showSlowRequestAlerts: false,
			showErrorAlerts: false
		});
	}
}

//#endregion
export { getRequestMonitor as t };